import {Helmet} from '@dr.pogodin/react-helmet';
import {showNotification} from 'platform/components';

import {defaultTo} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetDocumentContextCountQuery} from '@dms/api/core';
import {
  useGetIssueNoteCorrectionQuery,
  useGetWarehouseHeaderDynamicActionsQuery,
  usePostPrintoutOnDemandMutation,
  usePutWarehouseHeaderDynamicActionsMutation,
} from '@dms/api/warehouse';
import {useRefreshDocumentsCount} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  DetailTemplate,
  DetailTemplateHeader,
  NavigationItem,
  handleApiError,
  printPDF,
  useFileNotification,
  createHeaderActions,
} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {composePath, generateHashFromObjects, useNavigate} from 'shared/web';

import {Documents} from './(sections)/Documents/Documents';
import {Overview} from './(sections)/Overview/Overview';

export function IssueNoteCorrectionDetail() {
  const {issueNoteCorrectionId} = useWarehouseParams();

  const fileNotification = useFileNotification({onTimeout: () => showNotification.error()});

  const navigate = useNavigate();

  const {
    data: issueNoteCorrection,
    isLoading,
    isFetching,
    isError,
  } = useGetIssueNoteCorrectionQuery(
    {issueNoteCorrectionId: issueNoteCorrectionId as string},
    {skip: isNilOrEmpty(issueNoteCorrectionId)}
  );

  const {
    data: issueNoteCorrectionActions,
    isLoading: isIssueNoteCorrectionActionsLoading,
    isError: isIssueNoteCorrectionActionsError,
  } = useGetWarehouseHeaderDynamicActionsQuery(
    {id: issueNoteCorrectionId as string, resource: 'issue-note-correction'},
    {skip: isNilOrEmpty(issueNoteCorrectionId)}
  );

  const documentCountQuery = useGetDocumentContextCountQuery(
    {contextTarget: 'warehouse-issue-note-correction', contextId: issueNoteCorrectionId},
    {skip: !issueNoteCorrectionId}
  );

  const refreshDocumentsCount = useRefreshDocumentsCount({
    contextTarget: 'warehouse-issue-note-correction',
    contextId: issueNoteCorrectionId,
  });

  const [putIssueNoteCorrectionAction, {isLoading: isPutIssueNoteCorrectionActionLoading}] =
    usePutWarehouseHeaderDynamicActionsMutation();

  const [postPrintoutOnDemand, {isLoading: isPostPrintoutOnDemandLoading}] =
    usePostPrintoutOnDemandMutation();

  const isLoadingAll = isLoading || isIssueNoteCorrectionActionsLoading;
  const isErrorAll = isError || isIssueNoteCorrectionActionsError;

  const handleActionClick = (actionKey: string) => {
    if (actionKey === 'prntIssueNoteCorrection') {
      return postPrintoutOnDemand({
        body: {
          docOriginId: issueNoteCorrectionId as string,
          originEntityPrintoutType: 'prntIssueNoteCorrection',
        },
      })
        .unwrap()
        .then((data) => {
          const fileId = data?.printout?.fileId;
          const pdfUrl = data?.printout?.pdfUrl;

          if (fileId) {
            fileNotification.registerFile(fileId, {
              onSuccess: () => {
                if (pdfUrl) {
                  printPDF(pdfUrl);
                } else {
                  showNotification.error();
                }
              },
              onError: () => showNotification.error(),
            });
          } else {
            showNotification.error();
          }
        })
        .then(refreshDocumentsCount)
        .then(() =>
          showNotification.success(i18n.t('entity.warehouse.labels.documentWasGenerated'))
        )
        .then(() => {
          navigate(
            composePath(warehouseRoutes.issueNoteCorrectionDetailDocuments, {
              params: {id: issueNoteCorrectionId},
            })
          );
        })
        .catch(handleApiError);
    }

    return putIssueNoteCorrectionAction({
      id: issueNoteCorrectionId as string,
      resource: 'issue-note-correction',
      body: {actionKey},
    })
      .unwrap()
      .then(() => {
        refreshDocumentsCount();
      })
      .catch(handleApiError);
  };

  const headerActions = createHeaderActions({
    actions: issueNoteCorrectionActions?.actions,
    callback: handleActionClick,
    isLoading: isPutIssueNoteCorrectionActionLoading || isPostPrintoutOnDemandLoading,
  });

  const header: DetailTemplateHeader = {
    title: `${i18n.t('page.warehouse.labels.issueNoteCorrection')} ${defaultTo('', issueNoteCorrection?.issueNoteCorrection.number)}`,
    isEntityLoading: isFetching,
    actions: headerActions,
  };

  const navigation: NavigationItem[] = [
    {
      id: 'overview',
      label: i18n.t('general.labels.overview'),
      href: composePath(warehouseRoutes.issueNoteCorrectionDetailOverview, {
        params: {id: issueNoteCorrectionId},
      }),
      content: (
        <Overview
          issueNoteCorrection={issueNoteCorrection}
          isIssueNoteCorrectionLoading={isLoading}
          data-testid={testIds.warehouse.issueNoteCorrectionDetailOverview('section.overview')}
        />
      ),
      'data-testid': testIds.warehouse.issueNoteCorrectionDetailOverview('navigation.overview'),
    },
    {
      id: 'documents',
      label: i18n.t('general.labels.documents'),
      href: composePath(warehouseRoutes.issueNoteCorrectionDetailDocuments, {
        params: {id: issueNoteCorrectionId},
      }),
      content: (
        <Documents
          onDocumentAction={refreshDocumentsCount}
          data-testid={testIds.warehouse.issueNoteCorrectionDetailOverview('section.documents')}
        />
      ),
      badge: documentCountQuery.data?.count
        ? {value: documentCountQuery.data.count, colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.warehouse.issueNoteCorrectionDetailOverview('navigation.documents'),
    },
  ];

  return (
    <>
      <Helmet title={i18n.t('page.warehouse.labels.issueNoteCorrectionDetail')} />
      <DetailTemplate
        key={generateHashFromObjects(issueNoteCorrection)}
        isLoading={isLoadingAll}
        isError={isErrorAll}
        isLocked={isPutIssueNoteCorrectionActionLoading}
        header={header}
        navigation={navigation}
        data-testid={testIds.warehouse.issueNoteCorrectionDetailOverview('page')}
      />
    </>
  );
}
